import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";

const buildSections = (p) => [
  {
    n: "01",
    title: "Oggetto del contratto",
    body: "Le presenti condizioni regolano il noleggio degli scooter elettrici NIU NQiX 500 offerti da Vico's Scooter, con sede in Via Glira 4, 39036 San Cassiano in Badia (BZ), Italia. La prenotazione online effettuata sul sito costituisce proposta d'ordine che diventa contratto al momento della conferma del pagamento."
  },
  {
    n: "02",
    title: "Requisiti del conducente",
    body: "Il conducente deve avere almeno 18 anni compiuti e possedere patente di guida valida di categoria B (auto) oppure A1 (motociclo). All'atto del ritiro è obbligatorio esibire un documento d'identità in corso di validità e la patente. È vietato cedere lo scooter a terzi non autorizzati."
  },
  {
    n: "03",
    title: "Durata del noleggio e tariffe",
    body: `Le tariffe vigenti sono: €${p.half_day} per la mezza giornata (fino a 4 ore), €${p.full_day} per la giornata intera (9 ore), €${p.multi_day}/giorno per noleggi di 3 o più giorni consecutivi. Casco e bauletto sono sempre inclusi; ogni casco aggiuntivo €${p.extra_helmet} (massimo 2 caschi per scooter). Tutti gli importi sono IVA inclusa.`
  },
  {
    n: "04",
    title: "Pagamento e cauzione",
    body: "Il pagamento avviene online tramite carta di credito/debito o wallet digitale (Apple Pay, Google Pay) gestito da Stripe. È richiesta una pre-autorizzazione di €500 a titolo di cauzione, sbloccata entro 7 giorni dalla restituzione del veicolo in perfette condizioni."
  },
  {
    n: "05",
    title: "Modifica e cancellazione",
    body: "Cancellazione gratuita fino a 24 ore prima dell'orario di ritiro. Cancellazioni successive comportano l'addebito del 50% del totale. In caso di mancata presentazione (no-show) viene trattenuto l'intero importo. Le modifiche di data sono gratuite se richieste con almeno 24h di anticipo."
  },
  {
    n: "06",
    title: "Uso del veicolo",
    body: "Il conducente si impegna a guidare nel rispetto del Codice della Strada italiano, in particolare: indossare sempre il casco omologato, non superare i limiti di velocità, non guidare in stato di ebbrezza, non trasportare più di 1 passeggero (max 2 persone totali). È vietato l'uso fuori strada e l'utilizzo per competizioni."
  },
  {
    n: "07",
    title: "Responsabilità e assicurazione",
    body: "Il veicolo è coperto da assicurazione RCA. In caso di sinistro è obbligatorio compilare il modulo CAI e contattarci immediatamente al +39 335 6116528. Il conducente è responsabile dei danni causati per dolo, colpa grave, uso improprio o violazione del Codice della Strada. La franchigia massima è di €500."
  },
  {
    n: "08",
    title: "Furto e smarrimento",
    body: "In caso di furto è obbligatorio sporgere denuncia alle autorità competenti entro 24 ore e consegnarci copia della denuncia. La franchigia in caso di furto è di €1 000. Lo smarrimento di accessori (casco, chiavi) viene addebitato al conducente."
  },
  {
    n: "09",
    title: "Foro competente",
    body: "Per ogni controversia relativa al presente contratto è competente in via esclusiva il Foro di Bolzano. Il contratto è regolato dalla legge italiana. Per il consumatore residente in altro Stato UE valgono le tutele previste dal Regolamento (UE) 1215/2012."
  },
  {
    n: "10",
    title: "Contatti",
    body: "Vico's Scooter · Via Glira 4 · 39036 San Cassiano in Badia (BZ) · Italia · Email: info@vicotravel.it · Telefono: +39 335 6116528 · Instagram: @vicotravel.dolomites"
  },
];

export default function Terms() {
  const [pricing, setPricing] = useState({ half_day: 50, full_day: 85, multi_day: 70, extra_helmet: 15 });
  useEffect(() => {
    api.get("/scooters/niu-nqix-500").then(r => {
      const p = r.data?.pricing || {};
      setPricing({
        half_day: p.half_day ?? 50,
        full_day: p.full_day ?? 85,
        multi_day: p.multi_day ?? 70,
        extra_helmet: p.extra_helmet ?? 15,
      });
    }).catch(() => {});
  }, []);
  const sections = buildSections(pricing);
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-bold text-zinc-500 hover:text-scooter-yellow mb-12" data-testid="terms-back">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-scooter-yellow mb-4">Legale</div>
        <h1 className="font-heading font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9] mb-6">Termini e<br/>Condizioni</h1>
        <p className="text-zinc-400 mb-12 text-sm">Vico's Scooter · Versione del 1 settembre 2026</p>

        <div className="space-y-12">
          {sections.map(s => (
            <section key={s.n} className="border-l-2 border-scooter-yellow/40 pl-6" data-testid={`terms-section-${s.n}`}>
              <div className="font-heading text-stroke text-5xl font-black tracking-tighter mb-2">{s.n}</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight mb-4">{s.title}</h2>
              <p className="text-zinc-300 leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 p-6 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200/80 leading-relaxed">
          <strong className="text-amber-300">Nota informativa.</strong> Il presente documento riporta le condizioni generali del servizio. Si raccomanda di farlo revisionare da un legale prima della messa in produzione per garantire piena conformità a normative locali, GDPR e Codice del Consumo.
        </div>
      </div>
    </div>
  );
}
